import React from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { LayoutDashboard, ImageIcon, Video, Sparkles, Tag, Settings, LogOut, ExternalLink, Wallet, CalendarOff, Gift, Inbox } from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Bookings", icon: LayoutDashboard },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox, badgeKey: "new" },
  { to: "/admin/block", label: "Block Dates", icon: CalendarOff },
  { to: "/admin/finance", label: "Finance", icon: Wallet },
  { to: "/admin/photos", label: "Photos", icon: ImageIcon },
  { to: "/admin/videos", label: "Videos", icon: Video },
  { to: "/admin/amenities", label: "Amenities", icon: Sparkles },
  { to: "/admin/services", label: "Services", icon: Gift },
  { to: "/admin/rates", label: "Rates", icon: Tag },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [authed, setAuthed] = React.useState(false);
  const [inboxCount, setInboxCount] = React.useState(0);
  const username = localStorage.getItem("ibiza_admin_username") || localStorage.getItem("ibiza_admin_email");

  React.useEffect(() => {
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => {
      localStorage.removeItem("ibiza_admin_token");
      navigate("/admin/login", { replace: true });
    });
  }, [navigate]);

  // Poll inbox "new" count so the sidebar badge stays fresh across pages.
  // Also refetch immediately when the route changes (e.g. after marking a lead).
  const { pathname } = useLocation();
  React.useEffect(() => {
    if (!authed) return;
    const tick = () => api.get("/admin/leads/stats")
      .then((r) => setInboxCount(r.data?.new || 0))
      .catch(() => {});
    tick();
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [authed, pathname]);

  const logout = () => {
    localStorage.removeItem("ibiza_admin_token");
    localStorage.removeItem("ibiza_admin_username");
    localStorage.removeItem("ibiza_admin_email");
    navigate("/admin/login");
  };

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0A0B10] text-[#A1A4B5]">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0B10] text-white grid grid-cols-1 md:grid-cols-[260px_1fr]" data-testid="admin-shell">
      <aside className="border-r border-[var(--border)] p-6 flex flex-col gap-4 md:sticky md:top-0 md:h-screen">
        <div className="flex items-center gap-3 pb-6 border-b border-[var(--border)]">
          <img
            src="https://customer-assets.emergentagent.com/job_villa-booking-system-1/artifacts/suj9ewkw_65DC7DEC-C9BA-4743-8DA2-CAD0C86FB49C.png"
            alt="Ibiza Poolside Villa Resort"
            className="w-11 h-11 rounded-full object-contain"
          />
          <div>
            <div className="font-serif-display text-lg leading-tight">Ibiza Admin</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A4B5]">Control Room</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, end, label, icon: Icon, badgeKey }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/30"
                    : "text-[#A1A4B5] hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {badgeKey === "new" && inboxCount > 0 && (
                <span
                  className="ml-auto text-[10px] font-semibold rounded-full px-2 py-0.5 bg-[#C5A059] text-[#0A0B10]"
                  data-testid="inbox-nav-badge"
                >
                  {inboxCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto space-y-3">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-xs text-[#A1A4B5] hover:text-[#C5A059] transition-colors"
            data-testid="admin-view-site"
          >
            <ExternalLink size={14} /> View public site
          </a>
          <div className="text-xs text-[#A1A4B5] truncate">{username}</div>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="w-full bg-transparent border-[var(--border)] hover:bg-white/5"
            data-testid="admin-logout-button"
          >
            <LogOut size={14} className="mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      <main className="p-6 md:p-10 max-w-6xl w-full">
        <Outlet />
      </main>
    </div>
  );
}
